import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatWithPattern, getUserTimezone } from '@repo/common';
import { ChangePasswordDocument, DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { SettingsCard } from '@repo/ui/components/elements/settings-card/settings-card';
import { SettingsPageContent } from '@repo/ui/components/elements/settings-page-content/settings-page-content';
import { useCurrentUser } from '@repo/ui/contexts/current-user-context';
import { useDeviceType } from '@repo/ui/hooks/use-device-type';

import {
  ChangePasswordModal,
  type ChangePasswordPayload,
} from '../../../fixtures/userSettings/components/ChangePasswordModal/ChangePasswordModal';
import { MfaSettingsSection } from './mfa-settings-section';
import { SecuritySessionsSection } from './security-sessions-section';

import './security.scss';

export function SecurityPage() {
  const { t } = useTranslation('common');
  const { isMobile } = useDeviceType();
  const { currentUser, reloadCurrentUser } = useCurrentUser();
  const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = useState(false);
  const [successStep, setSuccessStep] = useState(0);
  const [passwordEstablishedLocally, setPasswordEstablishedLocally] = useState(false);
  const hasPassword = passwordEstablishedLocally || (currentUser?.hasPassword ?? true);
  const passwordChangedAt = currentUser?.passwordChangedAt;
  const passwordSubtitle =
    hasPassword && passwordChangedAt
      ? t('security.password.lastChanged', {
          date: formatWithPattern(passwordChangedAt, 'd MMMM yyyy', getUserTimezone()),
        })
      : t('security.password.setDescription');

  const [changePasswordMutation] = useMutation(ChangePasswordDocument);

  const handleOpenChangePasswordModal = () => {
    setIsPasswordChangeModalOpen(true);
  };

  const handleCloseChangePasswordModal = () => {
    setIsPasswordChangeModalOpen(false);
    setSuccessStep(0);
  };

  const handleChangePasswordSubmit: (payload: ChangePasswordPayload) => Promise<void> = async payload => {
    const wasFirstPassword = !hasPassword;

    await changePasswordMutation({
      variables: {
        input: {
          currentPassword: payload.currentPassword,
          newPassword: payload.newPassword,
        },
      },
    });

    if (wasFirstPassword) {
      setPasswordEstablishedLocally(true);
    }

    await reloadCurrentUser();
    setSuccessStep(wasFirstPassword ? 1 : 2);
    setIsPasswordChangeModalOpen(false);
  };

  return (
    <SettingsPageContent wide>
      <SDSFlexbox className="SecurityPage" direction="column" fullWidth>
        <SettingsCard className="SecurityPage__surface" direction="column">
          <SDSText dataTestId={DataTestIds.ADMIN.SIDEBAR.PAGE_TITLE} text={t('security.title')} type="h5" />
          <SettingsCard direction={isMobile ? 'column' : 'row'} variant="outlined">
            <SDSFlexbox direction="column" gap="text-small">
              <SDSText text={t('security.password.title')} type="h5" />
              <SDSText color="text-secondary" text={passwordSubtitle} type="body-medium" />
            </SDSFlexbox>
            <SDSButton
              ariaLabel={t('security.password.changeAria')}
              className="SecurityPage__action"
              dataTestId={DataTestIds.COMMON.SECURITY.CHANGE_PASSWORD_BTN}
              mode="outline"
              onClick={handleOpenChangePasswordModal}
              text={t('security.password.change')}
            />
          </SettingsCard>
          <MfaSettingsSection />
          <SecuritySessionsSection />
        </SettingsCard>
      </SDSFlexbox>
      <ChangePasswordModal
        hasPassword={hasPassword}
        isOpen={isPasswordChangeModalOpen}
        onClose={handleCloseChangePasswordModal}
        onSubmit={handleChangePasswordSubmit}
        successStep={successStep}
      />
    </SettingsPageContent>
  );
}

export { MfaSettingsSection } from './mfa-settings-section';
export { SecuritySessionsSection } from './security-sessions-section';
